import { ArrowForward, Search } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Grid, IconButton, Paper, Tab, Tabs, TextField, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridRenderCellParams } from "@mui/x-data-grid";
import { cloneDeep } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Api from "../../Api";
import { Feature, IPermissionContext, PermissionContext } from "../../contexts/PermissionContext";
import { IDispatchRequest, ITableOptions } from "../../interfaces";
import { Role } from "../../utils";
import { BreadcrumbBar } from "../Breadcrumb";
import { DispatchNoteDialog } from "../DispatchNoteDialog";
import { TabPanel } from "../Shared/TabPanel";
import "./index.scss";


export const Dispatches = ({ ...props }) => {

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [value, setValue] = useState(0);

    return (
        <div>
            <BreadcrumbBar
                current={"Dispatch"}
                previous={[]}
            />

            <Grid container sx={{ my: 3 }} alignItems="flex-end">
                <Grid item xs>
                    <Button
                        component={Link}
                        variant="contained"
                        to={"/dispatchrequest/create"}
                    >
                        New Dispatch Request
                    </Button>
                </Grid>
                <Grid item xs={"auto"}>
                    <TextField
                        label={<><Search /> Search</>}
                        variant="standard"
                        value={searchQuery}
                        type="search"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchQuery(e.target.value)
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container sx={{ my: 3 }}>
                <Grid item xs={12}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={value} onChange={(_, value) => setValue(value)} aria-label="d-table-tabs">
                            <Tab
                                label="Open"
                                id={`d-table-tab-${0}`} />
                            <Tab
                                label="Closed"
                                id={`d-table-tab-${1}`}
                            />
                        </Tabs>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <TabPanel value={value} index={0}>
                        <DispatchesTable
                            searchQuery={searchQuery}
                            open={true}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <DispatchesTable
                            searchQuery={searchQuery}
                            open={false}
                        />
                    </TabPanel>
                </Grid>
            </Grid>
        </div>
    );
}

export const DispatchesTable: React.FC<ITableOptions> = ({
    searchQuery,
    open,
    ...props }) => {

    useEffect(() => {

        Api.Dispatches.getByStatus(open ? "open" : "closed").then(x => {

            console.log("recieved", x);

            setDispatches(x as IDispatchRequest[])
            setDataLoading(false);
        });

    }, []);

    const [dispatches, setDispatches] = useState<IDispatchRequest[]>([]);
    const [dataLoading, setDataLoading] = useState<boolean>(true);

    const permissionContext = useContext(PermissionContext);


    const getColumns = () => {

        let displayColumns = cloneDeep(columns(permissionContext));


        if (!permissionContext.isRole(Role.Internal.AssetProtection)) {

            //hide the requestor columns if the user is not I-AP
            displayColumns = displayColumns.filter(x => !["requestor", "dateRequested"].includes(x.field))

            //hide the download if the user is not I-AP
            displayColumns = displayColumns.filter(x => !["download"].includes(x.field))

        }


        //hide the approved/closed date if the request is open
        if (open)
            displayColumns = displayColumns.filter(x => !["dateApproved", "dateClosed"].includes(x.field))

        return displayColumns;
    }

    return (
        <Grid container sx={{ height: '68vh' }}>
            <Paper sx={{ width: '100%' }} elevation={0}>

                <DataGrid
                    rows={rows(dispatches, searchQuery)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    getRowId={(row) => row.dispatchRequestId}
                    columns={getColumns()}
                    components={{ NoRowsOverlay: () => emptyTable(dataLoading) }}
                    disableSelectionOnClick
                    loading={dataLoading}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'dateCreated', sort: 'desc' }]
                        },
                        pagination: {
                            pageSize: 10
                        }
                    }}
                />
            </Paper>
        </Grid>

    );
}

const emptyTable = (dataLoading: boolean) => {
    return <GridOverlay> {dataLoading ? "Loading dispatch requests." : "No dispatch requests"}</GridOverlay>
}

const columns = (permissionContext: IPermissionContext) => {

    let cols: GridColDef[] = [
        {
            field: 'reference', headerName: 'Reference', flex: 1, renderCell: (params: GridRenderCellParams) => {
                return <Link to={"/dispatchrequest/update/" + params.row.dispatchRequestId}>{params.row.reference ?? "No Reference"}</Link>
            }
        },
        { field: 'statusName', headerName: 'Status', flex: 2 },
        {
            field: 'classes', headerName: 'Vehicle Class', flex: 1, renderCell: (params: GridRenderCellParams) => {
                return <Tooltip title={params.row.classes}>
                    <span>{params.row.classes}</span>
                </Tooltip>
            }
        },
        { field: 'requestor', headerName: 'Requested By', flex: 1 },

        {
            field: 'dateRequested',
            headerName: 'Requested',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateRequested) ? new Date(params.row?.dateRequested).toLocaleDateString('en-gb') : `` }
        },

        {
            field: 'dateCreated',
            headerName: 'Created',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateCreated) ? new Date(params.row?.dateCreated).toLocaleDateString('en-gb') : `` }
        },
        {
            field: 'dateApproved',
            headerName: 'Approved',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateApproved) ? new Date(params.row?.dateApproved).toLocaleDateString('en-gb') : `` }
        },
        {
            field: 'dateClosed',
            headerName: 'Closed',
            flex: 0,
            renderCell: (params) => { return (params.row?.dateClosed) ? new Date(params.row?.dateClosed).toLocaleDateString('en-gb') : `` }
        },

        {
            field: 'download', headerName: '', renderCell: (params: GridRenderCellParams) => {

                const isAssignable = [2, 3, 4, 5].includes(params.row.status);
                const isDispatchable = [4, 5, 6].includes(params.row.status);
                return <ButtonGroup size="small">

                    {permissionContext.isPermitted(Feature.DispatchRequest_Picklist) &&
                        <Button
                            disabled={!isAssignable}
                            onClick={() => { handlePicklist(params.row.dispatchRequestId) }}
                            variant="outlined">Pick List</Button>
                    }
                    {permissionContext.isPermitted(Feature.DispatchRequest_DispatchNote) &&
                        <DispatchNoteDialog
                            disabled={!isDispatchable}
                            dispatchRequestReference={params.row.reference}
                            dispatchRequestId={params.row.dispatchRequestId}
                        />
                    }


                </ButtonGroup>
            },
            width: 224
        },
        {
            field: 'edit', headerName: '', renderCell: (params: GridRenderCellParams) => {
                return <IconButton component={Link} to={"/dispatchrequest/update/" + params.row.dispatchRequestId}>
                    <ArrowForward />
                </IconButton>
            }
        }
    ];


    return cols;
}

const rows = (dispatches: IDispatchRequest[], searchQuery: string) => {

    return dispatches.filter(x => {
        let queryable = `${x.reference} ${x.statusName} ${new Date(x.dateCreated ?? "").toLocaleDateString('en-gb')}`;
        return queryable.includes(searchQuery);
    });
}

const handlePicklist = (id: string) => {
    Api.Dispatches.picklist(id)
        .then((response: any) => response.blob())
        .then((blob) => {

            const filename = `Pick List - ${new Date().toUTCString()}.pdf`;

            const url = window.URL.createObjectURL(
                new Blob([blob]),
            );

            const link: HTMLAnchorElement = document.createElement('a');

            link.href = url;
            link.setAttribute(
                'download',
                filename,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        });
}